import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { tv } from '@/utils/tv';
import { apiClient } from '@/api/client';
import { API_STUDENT_JOIN_OFFERS_DECLINE } from '@/api/endpoints';
import { STUDENT_JOIN_OFFERS_QUERY_KEY } from '@/api/queryKeys';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icons';
import { Modal } from '@/components/Modal';
import { Radio } from '@/components/Radio';
import { ROUTE_HOME } from '@/router/routes';
import { ButtonVariant, IconType, Size } from '@/types/components';
import {
  DeclineStudentJoinOfferReason,
  type StudentJoinOfferResponse,
} from '@/types/user';
import { ErrorMessage, logRequestError } from '@/utils/errors';

const reasonButton = tv({
  base: 'flex h-[54px] w-full cursor-pointer items-center justify-between gap-3 rounded-xl py-2 pr-3 pl-4 text-left transition-colors',
  variants: {
    isSelected: {
      true: 'bg-info-surface ring-[1.5px] ring-primary ring-inset',
      false: 'bg-on-primary-surface',
    },
  },
});

const reasonLabel = tv({
  base: 'text-subhead-14',
  variants: {
    isSelected: {
      true: 'text-primary',
      false: 'text-muted-foreground',
    },
  },
});

interface DeclineRequest {
  userPublicId: string;
  reason: string;
}

interface Props {
  userPublicId: string | null;
  onSuccess: () => void;
  onClose: () => void;
}

export const DeclineStudentOfferModal: React.FC<Props> = ({
  userPublicId,
  onSuccess,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedReason, setSelectedReason] =
    useState<DeclineStudentJoinOfferReason | null>(null);
  const [otherText, setOtherText] = useState('');

  const { mutateAsync: removeStudent, isPending } = useMutation({
    mutationFn: (payload: DeclineRequest) =>
      apiClient.post(API_STUDENT_JOIN_OFFERS_DECLINE, payload),
  });

  const isOpen = userPublicId !== null;
  const isOtherSelected =
    selectedReason === DeclineStudentJoinOfferReason.Other;
  const trimmedOtherText = otherText.trim();
  const hasValidReason =
    selectedReason !== null &&
    (!isOtherSelected || trimmedOtherText.length > 0);

  const resetForm = () => {
    setSelectedReason(null);
    setOtherText('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleStudentRemove = async () => {
    if (isPending || !hasValidReason || !userPublicId) {
      return;
    }

    const reason = isOtherSelected
      ? trimmedOtherText
      : t(`offers.decline.reasons.${selectedReason}`);

    try {
      await removeStudent({ userPublicId, reason });
      await queryClient.invalidateQueries({
        queryKey: STUDENT_JOIN_OFFERS_QUERY_KEY,
      });

      const remainingOffers = queryClient.getQueryData<
        StudentJoinOfferResponse[]
      >(STUDENT_JOIN_OFFERS_QUERY_KEY);

      handleClose();
      onSuccess();

      if (!remainingOffers || remainingOffers.length === 0) {
        navigate(ROUTE_HOME, { replace: true });
      }
    } catch (error) {
      logRequestError(ErrorMessage.DeclineStudentJoinOffer, error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      label={t('offers.decline.title')}
      onClose={handleClose}
    >
      <div className="flex shrink-0 flex-col gap-3 px-4 pt-[calc(24px+env(safe-area-inset-top))] pb-3 md:gap-2 md:px-5 md:pt-5 md:pb-4">
        <button
          type="button"
          onClick={handleClose}
          aria-label={t('global.back')}
          className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:hidden"
        >
          <Icon icon={IconType.ArrowLeft} />
        </button>
        <h2 className="text-title-36 text-foreground md:text-headline-20">
          {t('offers.decline.title')}
        </h2>
        <p className="text-body-14 text-subtle-foreground md:text-muted-foreground">
          {t('offers.decline.descriptionLead')}
          <span className="text-destructive">
            {t('offers.decline.descriptionEmphasis')}
          </span>
        </p>
      </div>

      <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 pb-4 [-ms-overflow-style:none] [scrollbar-width:none] md:px-5 [&::-webkit-scrollbar]:hidden">
        <p className="text-body-14 text-muted-foreground">
          {t('offers.decline.reasonHeader')}
        </p>
        <div className="flex flex-col gap-2 md:gap-3">
          {(
            Object.values(
              DeclineStudentJoinOfferReason,
            ) as DeclineStudentJoinOfferReason[]
          ).map((reason) => {
            const isSelected = reason === selectedReason;

            return (
              <button
                key={reason}
                type="button"
                onClick={() => setSelectedReason(reason)}
                className={reasonButton({ isSelected })}
              >
                <span className={reasonLabel({ isSelected })}>
                  {t(`offers.decline.reasons.${reason}`)}
                </span>
                <Radio checked={isSelected} />
              </button>
            );
          })}
        </div>

        {isOtherSelected && (
          <textarea
            value={otherText}
            onChange={(event) => setOtherText(event.target.value)}
            placeholder={t('offers.decline.otherHint')}
            rows={4}
            className="resize-none rounded-xl border border-border bg-on-primary-surface px-4 py-[15px] text-body-16 text-foreground placeholder:text-subtle-foreground outline-none"
          />
        )}
      </div>

      <div className="flex shrink-0 gap-3 border-t border-border bg-background px-4 pt-4 pb-[calc(24px+env(safe-area-inset-bottom))] md:border-t-0 md:p-5">
        <Button
          variant={ButtonVariant.Secondary}
          size={Size.Large}
          onClick={handleClose}
          className="flex-1"
        >
          {t('global.return')}
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          size={Size.Large}
          onClick={handleStudentRemove}
          disabled={!hasValidReason}
          isLoading={isPending}
          className="flex-1"
        >
          {t('global.decline')}
        </Button>
      </div>
    </Modal>
  );
};
